import * as React from 'react';
import { useMemo, useRef, useState } from 'react';

import { Option, showAllFilterOption, ValueSuggestions } from './AutoCompleteField.tsx';
import Field, { FieldProps } from './Field.tsx';
import { Tag } from './TagsField.tsx';
import classnames from '../../utils/classnames.tsx';
import { mergeRefs } from '../../utils/mergeRefs.tsx';

export interface MultiSelectOption extends Option {
    description?: string;
    leading?: React.ReactNode;
    /** Options with the same group are listed together under the group as a heading, in order of appearance. */
    group?: string;
    /** Merged into the option's tag once picked, e.g. the prompt editor's chip colours. */
    tagClassName?: string;
    /** Merged into the option's label in the list. */
    labelClassName?: string;
}

// a heading row above each group that still has matches
const withGroupHeadings = (options: MultiSelectOption[]): Option[] => {
    const groups = [...new Set(options.map((option) => option.group))];
    if (groups.every((group) => group === undefined)) return options;
    return groups.flatMap((group) => [
        ...(group === undefined ? [] : [{ role: 'heading', label: group, value: `__heading__${group}` }]),
        ...options.filter((option) => option.group === group),
    ]);
};

export interface MultiSelectFieldProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'onChange'>,
        Omit<FieldProps, 'value' | 'defaultValue' | 'onValueChange'> {
    options: MultiSelectOption[];
    value: string[];
    onValueChange: (value: string[]) => void;
    emptyMessage?: string;
    /** Accessible name of a tag's remove button, followed by the tag's label. */
    removeLabel?: string;
    /** When false, an option's `leading` node shows in the list only, not in its tag. */
    hasLeadingInTags?: boolean;
}

const renderOption = (option: MultiSelectOption) => (
    <span className="flex min-w-0 items-center gap-2">
        {option.leading}
        <span className="flex min-w-0 flex-col">
            <span className={classnames('truncate', option.labelClassName)}>{option.label}</span>
            {option.description && <span className="truncate text-xs text-content-weak">{option.description}</span>}
        </span>
    </span>
);

/** Typeahead over a fixed option list with several picks, shown as tags inside the field. Sibling of ComboboxField
 *  (one pick) and TagsField (free text); all three share AutoCompleteField's suggestion list. The list opens on
 *  click, typing or arrow keys, not on focus, and closes after a pick; Backspace in the empty input removes the last
 *  tag. Matching covers label and description (e.g. a person's email). */
const MultiSelectField = ({
    ref,
    value,
    onValueChange,
    options,
    emptyMessage,
    removeLabel,
    hasLeadingInTags = true,
    controls,
    className,
    readOnly,
    disabled,
    inputRef: propsInputRef = undefined,
    onFocus,
    onBlur,
    onKeyDown,
    ...props
}: MultiSelectFieldProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isOpen, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');

    const selected = value
        .map((candidate) => options.find((option) => option.value === candidate))
        .filter(Boolean) as MultiSelectOption[];
    // filtered here rather than by cmdk so an empty match list shows `emptyMessage`
    const matches = useMemo(() => {
        const needle = inputValue.trim().toLowerCase();
        return withGroupHeadings(
            options
                .filter((option) => !value.includes(option.value))
                .filter(
                    (option) =>
                        !needle ||
                        option.label.toLowerCase().includes(needle) ||
                        option.description?.toLowerCase().includes(needle)
                )
        );
    }, [options, value, inputValue]);
    const canOpen = !readOnly && !disabled;

    const handleOpenChange = (open: boolean) => setOpen(open && canOpen);
    const pick = (option: Option) => {
        onValueChange([...value, option.value]);
        setInputValue('');
        inputRef.current?.focus();
    };
    const remove = (candidate: string) => onValueChange(value.filter((item) => item !== candidate));

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Backspace' && !inputValue && value.length > 0) remove(value[value.length - 1]);
        onKeyDown?.(event);
    };

    return (
        <ValueSuggestions
            inputRef={inputRef}
            value={inputValue}
            // the input text is ours; a pick must not replace it (ValueSuggestions closes the list itself)
            onValueChange={() => undefined}
            isOpen={isOpen}
            onOpenChange={handleOpenChange}
            isDirty={inputValue !== ''}
            onSelectOption={pick}
            options={matches}
            filterOption={showAllFilterOption}
            renderOption={renderOption}
            emptyMessage={emptyMessage}
        >
            <Field className={className} readOnly={readOnly} disabled={disabled} ref={ref}>
                <Field.Input>
                    <div className="flex w-full min-w-0 flex-wrap items-center gap-1.5 p-1.5">
                        {selected.map((option) => (
                            <Tag
                                key={option.value}
                                className={classnames('py-0.5 text-xs', option.tagClassName)}
                                isRemovable={canOpen}
                                removeLabel={removeLabel ? `${removeLabel} ${option.label}` : undefined}
                                onRemove={() => remove(option.value)}
                            >
                                {hasLeadingInTags && option.leading}
                                {option.label}
                            </Tag>
                        ))}
                        <ValueSuggestions.Input
                            ref={mergeRefs(inputRef, propsInputRef)}
                            value={inputValue}
                            onValueChange={(text: string) => {
                                setInputValue(text);
                                if (!isOpen) handleOpenChange(true);
                            }}
                            onClick={() => !isOpen && handleOpenChange(true)}
                            onFocus={onFocus}
                            onBlur={(event) => {
                                handleOpenChange(false);
                                onBlur?.(event);
                            }}
                            onKeyDown={handleKeyDown}
                            className="flex-1 !min-w-20 bg-surface-transparent !p-0 !pl-1 outline-none"
                            readOnly={readOnly}
                            disabled={disabled}
                            {...props}
                        />
                    </div>
                </Field.Input>
                <Field.Controls>{controls}</Field.Controls>
            </Field>
        </ValueSuggestions>
    );
};

export default MultiSelectField;
